/**
 * Bidirectional converters between string values (used in SQLite/WS) and
 * protobuf enum values (used in gRPC messages).
 *
 * String-keyed maps use null-prototype objects to prevent prototype pollution
 * when indexing with untrusted strings (CLI flags, WS payloads, DB contents).
 */
import { EventType, TaskStatus, WorkspaceStatus, ClaudeProviderMode, ProviderToggle, } from "./gen/grackle/grackle_types_pb.js";
// ─── EventType ──────────────────────────────────────────────
const eventTypeToEnumMap = Object.assign(Object.create(null), {
    "": EventType.UNSPECIFIED,
    text: EventType.TEXT,
    tool_use: EventType.TOOL_USE,
    tool_result: EventType.TOOL_RESULT,
    error: EventType.ERROR,
    status: EventType.STATUS,
    system: EventType.SYSTEM,
    user_input: EventType.USER_INPUT,
    signal: EventType.SIGNAL,
    usage: EventType.USAGE,
    widget: EventType.WIDGET,
    turn_started: EventType.TURN_STARTED,
    turn_complete: EventType.TURN_COMPLETE,
    input_needed: EventType.INPUT_NEEDED,
});
const eventTypeToStringMap = {
    [EventType.UNSPECIFIED]: "",
    [EventType.TEXT]: "text",
    [EventType.TOOL_USE]: "tool_use",
    [EventType.TOOL_RESULT]: "tool_result",
    [EventType.ERROR]: "error",
    [EventType.STATUS]: "status",
    [EventType.SYSTEM]: "system",
    [EventType.USER_INPUT]: "user_input",
    [EventType.SIGNAL]: "signal",
    [EventType.USAGE]: "usage",
    [EventType.WIDGET]: "widget",
    [EventType.TURN_STARTED]: "turn_started",
    [EventType.TURN_COMPLETE]: "turn_complete",
    [EventType.INPUT_NEEDED]: "input_needed",
};
/** Convert a string event type to its proto enum value. */
export function eventTypeToEnum(s) {
    return eventTypeToEnumMap[s] ?? EventType.UNSPECIFIED;
}
/** Convert a proto enum event type to its string value. */
export function eventTypeToString(e) {
    return eventTypeToStringMap[e] ?? "";
}
// ─── TaskStatus ─────────────────────────────────────────────
const taskStatusToEnumMap = Object.assign(Object.create(null), {
    "": TaskStatus.UNSPECIFIED,
    // Canonical mappings
    not_started: TaskStatus.NOT_STARTED,
    working: TaskStatus.WORKING,
    paused: TaskStatus.PAUSED,
    complete: TaskStatus.COMPLETE,
    failed: TaskStatus.FAILED,
    // Backwards-compatible read mappings (old DB strings → new enums)
    pending: TaskStatus.NOT_STARTED,
    assigned: TaskStatus.NOT_STARTED,
    in_progress: TaskStatus.WORKING,
    waiting_input: TaskStatus.PAUSED,
    review: TaskStatus.PAUSED,
    done: TaskStatus.COMPLETE,
});
const taskStatusToStringMap = {
    [TaskStatus.UNSPECIFIED]: "",
    [TaskStatus.NOT_STARTED]: "not_started",
    [TaskStatus.WORKING]: "working",
    [TaskStatus.PAUSED]: "paused",
    [TaskStatus.COMPLETE]: "complete",
    [TaskStatus.FAILED]: "failed",
};
/** Convert a string task status to its proto enum value. */
export function taskStatusToEnum(s) {
    return taskStatusToEnumMap[s] ?? TaskStatus.UNSPECIFIED;
}
/** Convert a proto enum task status to its string value. */
export function taskStatusToString(e) {
    return taskStatusToStringMap[e] ?? "";
}
// ─── WorkspaceStatus ────────────────────────────────────────
const workspaceStatusToEnumMap = Object.assign(Object.create(null), {
    "": WorkspaceStatus.UNSPECIFIED,
    active: WorkspaceStatus.ACTIVE,
    archived: WorkspaceStatus.ARCHIVED,
});
const workspaceStatusToStringMap = {
    [WorkspaceStatus.UNSPECIFIED]: "",
    [WorkspaceStatus.ACTIVE]: "active",
    [WorkspaceStatus.ARCHIVED]: "archived",
};
/** Convert a string workspace status to its proto enum value. */
export function workspaceStatusToEnum(s) {
    return workspaceStatusToEnumMap[s] ?? WorkspaceStatus.UNSPECIFIED;
}
/** Convert a proto enum workspace status to its string value. */
export function workspaceStatusToString(e) {
    return workspaceStatusToStringMap[e] ?? "";
}
// ─── ClaudeProviderMode ─────────────────────────────────────
const claudeProviderModeToEnumMap = Object.assign(Object.create(null), {
    "": ClaudeProviderMode.UNSPECIFIED,
    off: ClaudeProviderMode.OFF,
    subscription: ClaudeProviderMode.SUBSCRIPTION,
    api_key: ClaudeProviderMode.API_KEY,
});
const claudeProviderModeToStringMap = {
    [ClaudeProviderMode.UNSPECIFIED]: "",
    [ClaudeProviderMode.OFF]: "off",
    [ClaudeProviderMode.SUBSCRIPTION]: "subscription",
    [ClaudeProviderMode.API_KEY]: "api_key",
};
/** Convert a string Claude provider mode to its proto enum value. */
export function claudeProviderModeToEnum(s) {
    return claudeProviderModeToEnumMap[s] ?? ClaudeProviderMode.UNSPECIFIED;
}
/** Convert a proto enum Claude provider mode to its string value. */
export function claudeProviderModeToString(e) {
    return claudeProviderModeToStringMap[e] ?? "";
}
// ─── ProviderToggle ─────────────────────────────────────────
const providerToggleToEnumMap = Object.assign(Object.create(null), {
    "": ProviderToggle.UNSPECIFIED,
    off: ProviderToggle.OFF,
    on: ProviderToggle.ON,
});
const providerToggleToStringMap = {
    [ProviderToggle.UNSPECIFIED]: "",
    [ProviderToggle.OFF]: "off",
    [ProviderToggle.ON]: "on",
};
/** Convert a string provider toggle to its proto enum value. */
export function providerToggleToEnum(s) {
    return providerToggleToEnumMap[s] ?? ProviderToggle.UNSPECIFIED;
}
/** Convert a proto enum provider toggle to its string value. */
export function providerToggleToString(e) {
    return providerToggleToStringMap[e] ?? "";
}
//# sourceMappingURL=enum-converters.js.map